import { DEFAULT_MONEY_SCALE, roundMoney, type MoneyScale } from '@shared/money';
import type { CartItem } from '../store/usePosStore';
import type { AppliedDiscount, CartEvaluationResult } from '../../promotions/public-api';
import type { PosDraftPayment, PosFiscalMode, PosPaymentMethodId } from '../domain/posTypes';
import { derivePosCheckoutTotals } from './posScreen.utils';
import { applyTipToCheckoutTotals, roundTipMoney } from './posCheckoutTip';
import { normalizePaymentDrafts, type NormalizedPaymentDraft } from './posCheckoutValidation';

export type PosCreateSaleDiscount = {
  id?: string;
  promotionId?: string | null;
  label: string;
  discountAmount: number;
  scope: AppliedDiscount['scope'] | 'manual';
  metadata?: Record<string, unknown>;
  appliedByUserId?: string | null;
};

export type PosCreateSaleRequest = {
  branchId: string;
  total: number;
  subtotal?: number;
  discountTotal?: number;
  /** Idempotency: reuse saleId across retries of the same checkout attempt. */
  saleId?: string;
  /** Remote POS draft consumed atomically by the new sale command. */
  saleDraftId?: string | null;
  cashSessionId: string | null;
  cashierUserId: string | null;
  fiscalMode: PosFiscalMode;
  fiscalBuyer: {
    fiscalBuyerCuit?: string | null;
    fiscalBuyerName?: string | null;
    fiscalBuyerTaxCondition?: number | null;
    fiscalInvoiceType?: number | null;
  } | null;
  customerId?: string | null;
  /** Lista de precio activa en el checkout — el server la re-resuelve
   *  para validar unitPriceOriginal (price_lists P1). */
  priceListId?: string | null;
  items: Array<{
    productId: string;
    variantId?: string | null;
    quantity: number;
    price: number;
    name: string;
    sku?: string;
    itemType: CartItem['itemType'];
    packageId?: string;
    affectsStock: boolean;
    unitPriceOriginal?: number;
    /** Para `assert_promo_discount_ceilings` (targets category/brand). */
    categoryId?: string | null;
    brandId?: string | null;
    discountAmount?: number;
    promotionId?: string | null;
    unitOfMeasure?: string | null;
    options?: Array<{
      groupId: string;
      groupName: string;
      choiceId: string;
      choiceName: string;
      priceDelta: number;
      qty: number;
      linkedProductId?: string | null;
      modifierKind?: 'extra' | 'exclude' | 'detail' | null;
      allergenTags?: string[];
      kitchenLabel?: string | null;
      substitutesChoiceId?: string | null;
      substitutesProductId?: string | null;
    }> | null;
    lineNote?: string | null;
    performedByUserId?: string | null;
    soldByUserId?: string | null;
  }>;
  discounts?: PosCreateSaleDiscount[];
  /** ADR-030: intención de promo (códigos / cashier); montos los decide el server. */
  promoIntent?: {
    promoCodes?: string[];
    selectedPromotionIds?: string[];
  };
  payments: Array<{
    id?: string;
    methodId: PosPaymentMethodId;
    /** Monto aplicado a la venta (el vuelto NO entra al cajón). */
    amount: number;
    /** Solo cash: efectivo entregado por el cliente; vuelto = tenderedAmount - amount. */
    tenderedAmount?: number;
    customerId?: string;
    status: 'pending' | 'confirmed';
    reference?: string;
    metadata?: Record<string, unknown>;
  }>;
  supervisorOverrideId?: string;
  supervisorOverrides?: {
    discount?: string;
    credit?: string;
  };
  diningTableId?: string | null;
  /** QSR handoff: nombre para retirar (opcional). */
  pickupName?: string | null;
  /** QSR handoff: pickup | dine_in */
  fulfillmentType?: 'pickup' | 'dine_in' | 'delivery';
  /** Propina salón incluida en total cobrado (no rider_tip). */
  tipAmount?: number;
  /** Quién atendió el ticket (métricas). */
  primaryStaffUserId?: string | null;
};

function discountKey(d: AppliedDiscount): string {
  return `${d.promotionId}:${d.scope}:${d.lineId ?? ''}:${d.discountAmount}`;
}

function discountFromApplied(d: AppliedDiscount): PosCreateSaleDiscount {
  return {
    promotionId: d.promotionId,
    label: d.promotionName,
    discountAmount: d.discountAmount,
    scope: d.scope,
    metadata: d.lineId ? { lineId: d.lineId } : undefined,
  };
}

function buildSaleDiscountsFromEvaluation(evaluation: CartEvaluationResult): PosCreateSaleDiscount[] {
  const map = new Map<string, PosCreateSaleDiscount>();
  for (const line of evaluation.lines) {
    for (const d of line.appliedDiscounts) {
      const k = discountKey(d);
      if (!map.has(k)) map.set(k, discountFromApplied(d));
    }
  }
  for (const d of evaluation.cartDiscounts) {
    const k = discountKey(d);
    if (!map.has(k)) map.set(k, discountFromApplied(d));
  }
  return Array.from(map.values());
}

/**
 * Reparte el vuelto (totalIngresado − total) entre los borradores cash, de
 * atrás hacia adelante. Devuelve índice → monto aplicado para cada cash draft
 * que recibe vuelto. Invariante del checkout: sobrepago solo es posible con
 * cash (validateCheckoutPaidVersusTotal), así que todo el vuelto sale de
 * efectivo y el resto de medios queda intacto.
 */
function computeCashAppliedAmounts(
  drafts: NormalizedPaymentDraft[],
  total: number,
  scale: MoneyScale,
): Map<number, number> {
  const applied = new Map<number, number>();
  const totalIngresado = roundMoney(
    drafts.reduce((sum, d) => sum + d.amountNumber, 0),
    scale,
  );
  let change = roundMoney(Math.max(0, totalIngresado - total), scale);
  if (change <= 0) return applied;
  for (let i = drafts.length - 1; i >= 0 && change > 0; i -= 1) {
    const draft = drafts[i];
    if (!draft || draft.methodId !== 'cash') continue;
    const draftApplied = roundMoney(Math.max(0, draft.amountNumber - change), scale);
    applied.set(i, draftApplied);
    change = roundMoney(change - (draft.amountNumber - draftApplied), scale);
  }
  return applied;
}

function buildPaymentFromDraft(draft: NormalizedPaymentDraft, cashAppliedAmount?: number) {
  const paymentId = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(draft.id)
    ? draft.id
    : undefined;
  if (draft.methodId === 'mercadopago_qr') {
    return {
      ...(paymentId ? { id: paymentId } : {}),
      methodId: draft.methodId as PosPaymentMethodId,
      amount: draft.amountNumber,
      customerId: draft.customerId,
      status: 'pending' as const,
      reference: `qr-${crypto.randomUUID()}`,
      metadata: { channel: 'mercadopago', state: 'waiting_scan' },
    };
  }
  if (draft.methodId === 'cheque') {
    const checkNumber = draft.checkNumber?.trim();
    return {
      ...(paymentId ? { id: paymentId } : {}),
      methodId: draft.methodId as PosPaymentMethodId,
      amount: draft.amountNumber,
      customerId: draft.customerId,
      status: 'confirmed' as const,
      reference: checkNumber ? `check-${checkNumber}` : undefined,
      metadata: {
        check: {
          number: checkNumber ?? null,
          dueDate: draft.checkDueDate?.trim() || null,
          drawer: draft.checkDrawer?.trim() || null,
          bank: draft.checkBank?.trim() || null,
        },
      },
    };
  }
  return {
    ...(paymentId ? { id: paymentId } : {}),
    methodId: draft.methodId as PosPaymentMethodId,
    amount: cashAppliedAmount ?? draft.amountNumber,
    ...(draft.methodId === 'cash' ? { tenderedAmount: draft.amountNumber } : {}),
    customerId: draft.customerId,
    status: 'confirmed' as const,
  };
}

/** Mapeo puro carrito + borradores de cobro → payload de persistencia (testeable sin SQLite/RN). */
export function mapPosCreateSaleRequest(params: {
  branchId: string;
  cart: CartItem[];
  paymentDrafts: PosDraftPayment[];
  saleFiscalMode: PosFiscalMode;
  activeCashSessionId: string | null;
  cashierUserId: string | null;
  forceAllowSale: boolean;
  fiscalBuyer?: PosCreateSaleRequest['fiscalBuyer'];
  cartEvaluation?: CartEvaluationResult | null;
  supervisorOverrideId?: string;
  supervisorOverrides?: {
    discount?: string;
    credit?: string;
  };
  diningTableId?: string | null;
  extraDiscounts?: PosCreateSaleDiscount[];
  extraDiscountTotal?: number;
  saleCustomerId?: string | null;
  /** Lista de precio activa del checkout (ver PosCreateSaleRequest.priceListId). */
  priceListId?: string | null;
  /** Idempotency: reuse saleId across retries of the same checkout attempt. */
  saleId?: string;
  saleDraftId?: string | null;
  promoIntent?: PosCreateSaleRequest['promoIntent'];
  pickupName?: string | null;
  fulfillmentType?: PosCreateSaleRequest['fulfillmentType'];
  tipAmount?: number;
  primaryStaffUserId?: string | null;
  moneyScale?: MoneyScale;
}): { input: PosCreateSaleRequest; options: { forceAllowSale: boolean } } {
  const scale = params.moneyScale ?? DEFAULT_MONEY_SCALE;
  const merchandise = derivePosCheckoutTotals(
    params.cart,
    params.paymentDrafts,
    params.cartEvaluation,
    params.extraDiscountTotal ?? 0,
    scale,
  );
  const tipAmount = roundTipMoney(params.tipAmount ?? 0);
  const { total, subtotal, discountTotal } = applyTipToCheckoutTotals(merchandise, tipAmount);
  const normalizedPayments = normalizePaymentDrafts(params.paymentDrafts, scale);
  // El vuelto no entra al cajón: cada cash draft lleva amount=aplicado y
  // tenderedAmount=entregado (server: pos.sale_payments.tendered_amount).
  const cashAppliedByDraftIndex = computeCashAppliedAmounts(normalizedPayments, total, scale);
  const evaluatedByLineId = new Map(
    (params.cartEvaluation?.lines ?? []).map((line) => [line.lineId, line])
  );

  const items = params.cart.map((item) => {
    const evaluated = evaluatedByLineId.get(item.lineId);
    const price = evaluated?.unitPrice ?? item.price;
    const primaryPromotion = evaluated?.appliedDiscounts[0];
    return {
      productId: item.productId,
      variantId: item.variantId ?? null,
      quantity: item.quantity,
      price,
      name: item.name,
      sku: item.sku,
      itemType: item.itemType,
      ...(item.packageId ? { packageId: item.packageId } : {}),
      affectsStock: item.affectsStock,
      unitPriceOriginal: evaluated?.unitPriceOriginal ?? item.price,
      categoryId: item.category?.trim() ? item.category : null,
      brandId: item.brandId ?? null,
      discountAmount: evaluated?.lineDiscountTotal ?? 0,
      promotionId: primaryPromotion?.promotionId ?? null,
      unitOfMeasure: item.unitOfMeasure ?? null,
      options: item.menuOptions?.length
        ? item.menuOptions.map((o) => ({
            groupId: o.groupId,
            groupName: o.groupName,
            choiceId: o.choiceId,
            choiceName: o.choiceName,
            priceDelta: o.priceDelta,
            qty: o.qty,
            linkedProductId: o.linkedProductId ?? null,
            modifierKind: o.modifierKind ?? null,
            allergenTags: o.allergenTags ?? [],
            kitchenLabel: o.kitchenLabel ?? null,
            substitutesChoiceId: o.substitutesChoiceId ?? null,
            substitutesProductId: o.substitutesProductId ?? null,
          }))
        : null,
      lineNote: item.lineNote?.trim() || null,
      performedByUserId: item.performedByUserId ?? null,
      soldByUserId: item.soldByUserId ?? null,
    };
  });

  const promoDiscounts =
    params.cartEvaluation && params.cartEvaluation.discountTotal > 0
      ? buildSaleDiscountsFromEvaluation(params.cartEvaluation)
      : [];
  const discounts = [...promoDiscounts, ...(params.extraDiscounts ?? [])];
  const discountsOut = discounts.length > 0 ? discounts : undefined;

  return {
    input: {
      branchId: params.branchId,
      total,
      subtotal: (params.cartEvaluation || (params.extraDiscountTotal ?? 0) > 0) ? subtotal : undefined,
      discountTotal: (params.cartEvaluation || (params.extraDiscountTotal ?? 0) > 0) ? discountTotal : undefined,
      saleId: params.saleId,
      cashSessionId: params.activeCashSessionId,
      cashierUserId: params.cashierUserId,
      fiscalMode: params.saleFiscalMode,
      saleDraftId: params.saleDraftId ?? null,
      fiscalBuyer: params.fiscalBuyer ?? null,
      priceListId: params.priceListId ?? null,
      items,
      discounts: discountsOut,
      ...(params.promoIntent ? { promoIntent: params.promoIntent } : {}),
      payments: normalizedPayments.map((draft, index) =>
        buildPaymentFromDraft(draft, cashAppliedByDraftIndex.get(index)),
      ),
      diningTableId: params.diningTableId ?? null,
      pickupName: params.pickupName?.trim() || null,
      fulfillmentType: params.fulfillmentType,
      tipAmount: tipAmount > 0 ? tipAmount : 0,
      primaryStaffUserId: params.primaryStaffUserId ?? null,
      customerId: params.saleCustomerId ?? null,
      ...(params.supervisorOverrideId
        ? { supervisorOverrideId: params.supervisorOverrideId }
        : {}),
      ...(params.supervisorOverrides
        ? { supervisorOverrides: params.supervisorOverrides }
        : {}),
    },
    options: { forceAllowSale: params.forceAllowSale },
  };
}
